import { bookActions } from '@/actions';
import { useAppSelector } from '@/hooks';
import { createContext, useContext, useReducer } from 'react';
import { useDispatch } from 'react-redux';

export type CheckoutModalScreen =
  | 'cof'
  | 'applyGiftcard'
  | 'applyValuecard'
  | 'applyWellnesscard'
  | 'bokadirektPay'
  | 'bokadirektAddCard'
  | 'klarnaOptions'
  | 'walleyPhoneInput';

export type CheckoutModalState = {
  isOpen: boolean;
  screen: CheckoutModalScreen;
};

type CheckoutModalAction =
  | {
      type: 'SHOW_MODAL';
      payload: CheckoutModalScreen | null;
    }
  | {
      type: 'CLOSE_MODAL';
    };

function checkoutModalReducer(state: CheckoutModalState, action: CheckoutModalAction) {
  switch (action.type) {
    case 'CLOSE_MODAL':
      return {
        ...state,
        isOpen: false,
      };

    case 'SHOW_MODAL': {
      return {
        ...state,
        isOpen: true,
        screen: action.payload,
      };
    }

    default:
      return state;
  }
}

export type UseCheckoutModalManagerResult = ReturnType<typeof useCheckoutModalManager>;

const useCheckoutModalManager = () => {
  const [state, dispatch] = useReducer(checkoutModalReducer, {
    isOpen: false,
    screen: undefined,
  });

  const globalDispatch = useDispatch();
  const { errorGiftCard } = useAppSelector((state) => state.book);

  const handleOnClose = () => {
    if (errorGiftCard) globalDispatch(bookActions.clearGiftCardMessages());
    dispatch({ type: 'CLOSE_MODAL' });
  };

  const handleOnOpen = (screen?: CheckoutModalScreen) => {
    dispatch({ type: 'SHOW_MODAL', payload: screen });
  };

  return {
    isOpen: state.isOpen,
    screen: state.screen,
    onClose: handleOnClose,
    onOpen: handleOnOpen,
  };
};

const CheckoutModalContext = createContext<UseCheckoutModalManagerResult>({
  isOpen: false,
  screen: undefined,
  onClose: () => {},
  onOpen: () => {},
});

export const useCheckoutModal = () => {
  const context = useContext(CheckoutModalContext);

  if (!context) {
    throw new Error('useCheckoutModal must be used within CheckoutModalProvider');
  }

  return context;
};

export const CheckoutModalProvider = ({ children }: { children: React.ReactNode }) => {
  return <CheckoutModalContext.Provider value={useCheckoutModalManager()}>{children}</CheckoutModalContext.Provider>;
};
